<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: App                                                   |
| Section: src                                                     |
| Purpose: Root application shell and route container.             |
+------------------------------------------------------------------+
-->
<template>
  <MessageBar v-if="!shouldHideNavAndFooter" :message="$t('messageBar.message')" />

  <!-- message="Bezig met onderhoud. Tijdelijke storingen mogelijk. Dank voor je begrip!" /> -->
  <NavBar v-if="!isHomeRoute && !shouldHideNavAndFooter" />
  <PrerenderNavigation v-if="isPrerenderRequest" />
  <MalukuPrerenderSnapshot v-if="isPrerenderRequest && $route.name === 'MalukuOverview'" />
  <PublicPagePrerenderSnapshot v-else-if="showPublicPrerenderSnapshot" />
  <router-view v-else />
  <MachineTranslationNotice v-if="!shouldHideNavAndFooter" />
  <SiteFooter v-if="!shouldHideNavAndFooter" />
</template>

<script>
import { ref, computed, onBeforeUnmount, onMounted, provide, watch } from 'vue';
import { useRoute } from 'vue-router';
import NavBar from './components/NavBar.vue';
import SiteFooter from './components/SiteFooter.vue';
import MessageBar from './components/MessageBar.vue';
import PublicPagePrerenderSnapshot from './components/prerender/PublicPagePrerenderSnapshot.vue';
import MalukuPrerenderSnapshot from './components/prerender/MalukuPrerenderSnapshot.vue';
import PrerenderNavigation from './components/prerender/PrerenderNavigation.vue';
import MachineTranslationNotice from './components/MachineTranslationNotice.vue';
import authenticatedApiClient from './api/authenticatedApiClient';
import { fetchCurrentSession } from './api/securityAuditService';
import { SUPPORTED_LOCALES } from './composables/useLocale';
import {
  clearFrontendAuthSession,
  FRONTEND_BUILD_ID,
  getStoredFrontendBuildId,
  setStoredFrontendBuildId,
} from './utils/frontendAuthSession';
import { clearMachineTranslationDetected } from './utils/machineTranslationNotice';

export default {
  name: 'App',
  components: {
    NavBar,
    SiteFooter,
    MessageBar,
    MachineTranslationNotice,
    PublicPagePrerenderSnapshot,
    MalukuPrerenderSnapshot,
    PrerenderNavigation,
  },
  setup() {
    const route = useRoute();

    // Treat locale roots (/nl, /en, /id) as home too to avoid double navbars.
    const localizedHomeRegex = new RegExp(`^/(?:${SUPPORTED_LOCALES.join('|')})/?$`);
    const isHomeRoute = computed(() => route.path === '/' || localizedHomeRegex.test(route.path));

    // Hide app chrome for dedicated print view as well.
    const isPrerenderRequest = computed(() => route.query.prerender === '1');
    const shouldHideNavAndFooter = computed(() =>
      route.meta.hideNavAndFooter === true ||
      route.query.print === 'true' ||
      isPrerenderRequest.value
    );
    const showPublicPrerenderSnapshot = computed(() =>
      isPrerenderRequest.value
      && route.name !== 'PersonPage'
      && route.name !== 'BlogPost'
      && !route.meta.maluku
    );

    watch(() => route.fullPath, () => {
      clearMachineTranslationDetected();
    });

    // Authentication state
    const authToken = ref(localStorage.getItem('authToken') || sessionStorage.getItem('authToken'));
    const username = ref(localStorage.getItem('username') || sessionStorage.getItem('username'));
    const isAdmin = ref(false);
    let sessionRequestVersion = 0;

    const resolveSessionAccess = async (token) => {
      const requestVersion = ++sessionRequestVersion;
      isAdmin.value = false;
      if (!token) return;

      try {
        const session = await fetchCurrentSession();
        if (requestVersion !== sessionRequestVersion || authToken.value !== token) return;
        username.value = session.username || username.value;
        isAdmin.value = session.isAdmin === true;
      } catch {
        if (requestVersion === sessionRequestVersion) {
          isAdmin.value = false;
        }
      }
    };

    watch(authToken, resolveSessionAccess, { immediate: true });

    const clearAuthState = () => {
      authToken.value = null;
      username.value = null;
      isAdmin.value = false;
      clearFrontendAuthSession();
    };

    // Login function
    const login = (user, token, rememberMe) => {
      clearFrontendAuthSession();
      authToken.value = token;
      username.value = user;
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('authToken', token);
      storage.setItem('username', user);
    };

    // Logout function
    const logout = async () => {
      try {
        await authenticatedApiClient.post('/logout');
      } catch (error) {
        console.error('Unable to clear the server authentication session:', error);
      } finally {
        clearAuthState();
        window.location.reload();
      }
    };

    let buildPollIntervalId = null;

    const checkDeployedBuild = async () => {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        const response = await fetch('/build-meta.json', {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          return;
        }

        const metadata = await response.json();
        const deployedBuildId = typeof metadata.buildId === 'string' ? metadata.buildId : '';

        if (!deployedBuildId) {
          return;
        }

        if (deployedBuildId === FRONTEND_BUILD_ID) {
          setStoredFrontendBuildId(deployedBuildId);
          return;
        }

        if (getStoredFrontendBuildId() === deployedBuildId) {
          return;
        }

        setStoredFrontendBuildId(deployedBuildId);
        window.location.reload();
      } catch {
        // A transient metadata fetch failure should not interrupt the app.
      }
    };

    onMounted(() => {
      if (process.env.NODE_ENV !== 'production') {
        return;
      }

      buildPollIntervalId = window.setInterval(checkDeployedBuild, 60000);
      checkDeployedBuild();
    });

    onBeforeUnmount(() => {
      if (buildPollIntervalId) {
        window.clearInterval(buildPollIntervalId);
      }
    });

    // Provide authentication state and methods to child components
    provide('authToken', authToken);
    provide('username', username);
    provide('isAdmin', isAdmin);
    provide('login', login);
    provide('logout', logout);

    return {
      isHomeRoute,
      isPrerenderRequest,
      shouldHideNavAndFooter,
      showPublicPrerenderSnapshot,
      authToken,
      username,
      isAdmin,
      login,
      logout
    };
  }
};
</script>

<style>
.no-scroll {
  overflow: hidden;
  position: fixed;
  width: 100%;
}
</style>
